import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Calendar, Trophy, Users } from "lucide-react";
import CTASection from "../components/CTASection";

export default function Community() {
  return (
    <div data-testid="community-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="absolute -top-32 right-1/4 w-96 h-96 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#1B4332]/10 text-black text-xs font-semibold mb-6">Community</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            12,000 business owners.<br />
            <span className="italic text-black/60 font-medium">One unstoppable network.</span>
          </h1>
          <p className="mt-7 text-lg text-black/70 max-w-2xl mx-auto leading-relaxed">
            Connect with founders just like you. Swap playbooks, share wins, ask anything — and grow together.
          </p>
          <div className="mt-9">
            <Link to="/register" className="btn-primary">Join the community <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <MessageCircle size={22} />, t: "Forums", d: "Searchable discussions across 30+ topics. Find an answer or start a thread." },
              { icon: <Users size={22} />, t: "Peer mentoring", d: "Get matched with a more-experienced Brivo merchant for monthly 1:1s." },
              { icon: <Calendar size={22} />, t: "Events", d: "Monthly workshops, AMAs, and an annual user conference in Lagos." },
              { icon: <Trophy size={22} />, t: "Awards", d: "Annual Brivo Awards celebrating the most innovative merchants on the platform." },
            ].map((c) => (
              <div key={c.t} className="bg-[#FFFCF2] rounded-3xl p-7 border border-[#1B4332]/8 card-hover">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-black mb-5">{c.icon}</div>
                <h3 className="font-display font-bold text-xl text-black mb-2">{c.t}</h3>
                <p className="text-sm text-black/70 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voices */}
      <section className="py-24 md:py-32 bg-[#FFE4E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">Voices from the community</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "Adunni", q: "I asked the forum about cross-border shipping and got 14 replies in an hour. Better than any course I've paid for.", img: "https://images.unsplash.com/photo-1758887261865-a2b89c0f7ac5?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
              { n: "Marcus", q: "The peer mentoring program connected me with someone running a chain twice my size. Game-changing perspective.", img: "https://images.unsplash.com/photo-1765648636178-60e73bcc865e?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
              { n: "Priya", q: "I won the Brivo Awards last year. The exposure brought 800 new customers in a month.", img: "https://images.unsplash.com/photo-1769636930047-4478f12cf430?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
            ].map((p) => (
              <div key={p.n} className="bg-white rounded-3xl p-8 border border-[#1B4332]/5 card-hover">
                <p className="text-black text-lg leading-relaxed font-medium mb-7">"{p.q}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-[#1B4332]/10">
                  <img src={p.img} alt={p.n} className="w-12 h-12 rounded-full object-cover" />
                  <div className="font-semibold text-black">{p.n}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-[#1B4332] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Upcoming events</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5 text-left">
            {[
              { d: "Apr 5", t: "AMA with Adunni Ola", loc: "Online · 2pm UTC" },
              { d: "Apr 18", t: "Multi-branch workshop", loc: "Online · 4pm UTC" },
              { d: "May 22", t: "Brivo Connect Lagos", loc: "Eko Hotel · All day" },
            ].map((e) => (
              <div key={e.t} className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-6">
                <div className="font-display font-extrabold text-3xl">{e.d}</div>
                <div className="font-semibold mt-2">{e.t}</div>
                <div className="text-sm text-white/60 mt-1">{e.loc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection accent="cream" title="Join 12,000 merchants" subtitle="Real talk, real tactics, real friendships. The Brivo community is one of the best parts of using us." />
    </div>
  );
}
